import { Button, Card, Icon, InputGroup, Spinner, Tag } from '@blueprintjs/core';
import useAssistantChat from 'ai-assistant/hooks/useAssistantChat';
import { ChatSessionSummary, ESETHistoryService, ModelType } from 'ai-assistant/services/chat-history-service';
import { UUID } from 'ai-assistant/utils/uuid';
import ModelTypeTag from 'components/ModelTypeTag';
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type MouseEvent, type SetStateAction, type Dispatch } from 'react';
import { useDispatch } from 'react-redux';
import { setCurrentModel } from 'controllers/common/action';
import { constructPath, findRoute, routes } from 'routes';

import './AssistantHistory.light.scss';

type SortOption = 'recent' | 'oldest' | 'tool';

type NoInfer<T> = [T][T extends any ? 0 : never];

interface AssistantHistoryProps {
  chat: ReturnType<typeof useAssistantChat>;
  onSessionResume?: () => void;
}

const sortLabels: Record<SortOption, string> = {
  recent: 'Most Recent',
  oldest: 'Oldest First',
  tool: 'By Application',
};

const navigateToModelRoute = () => {
  const modelPath = constructPath(findRoute(routes, 'Model'));
  if (!modelPath) {
    return;
  }

  window.location.hash = modelPath;
};

export function AssistantHistory({ chat, onSessionResume }: AssistantHistoryProps) {
  const historyService = useMemo(() => new ESETHistoryService(), []);
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [activeLoadingId, setActiveLoadingId] = useState<UUID | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<UUID | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setIfMounted = useCallback(<T,>(setter: Dispatch<SetStateAction<T>>, value: SetStateAction<NoInfer<T>>) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    if (chat.isModelPage && !chat.currentModelAssociation?.modelKey) {
      setIfMounted(setSessions, []);
      setIfMounted(setLoadingSessions, false);
      setIfMounted(setError, null);
      return;
    }

    setIfMounted(setLoadingSessions, true);
    setIfMounted(setError, null);
    try {
      const result = await historyService.fetchSessions(
        chat.isModelPage ? chat.currentModelAssociation : undefined
      );
      setIfMounted(setSessions, result);
    } catch (err) {
      console.error('Failed to fetch chat sessions', err);
      setIfMounted(setError, 'Unable to load chat history right now.');
    } finally {
      setIfMounted(setLoadingSessions, false);
    }
  }, [chat.currentModelAssociation, chat.isModelPage, historyService, setIfMounted]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions, chat.sessionId, chat.sessionTitle]);

  const filteredSessions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const baseSessions = normalizedSearch.length === 0
      ? sessions
      : sessions.filter(session => {
        const searchableValues = [
          session.sessionName,
          session.modelName,
          session.draftId,
          session.modelType,
          session.modelKey,
          ...(session.modelTypes ?? []),
        ];
        return searchableValues.some(value => String(value ?? '').toLowerCase().includes(normalizedSearch));
      });

    const sortedSessions = [...baseSessions];
    sortedSessions.sort((a, b) => {
      if (a.isPinned !== b.isPinned) {
        return a.isPinned ? -1 : 1;
      }
      if (sortBy === 'oldest') {
        return a.timestamp - b.timestamp;
      }
      if (sortBy === 'tool') {
        return a.sessionName.localeCompare(b.sessionName);
      }
      return b.timestamp - a.timestamp;
    });

    return sortedSessions;
  }, [sessions, search, sortBy]);

  const formatDate = (timestamp: number) => {
    if (!timestamp) {
      return 'Unknown date';
    }
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const renderModelPills = (modelTypes: ModelType[] | null | undefined) => {
    if (!modelTypes || modelTypes.length === 0) {
      return null;
    }

    return (
      <span className='assistant-history__model-pill-group'>
        {modelTypes.map((modelType, index) => {
          const normalizedType = String(modelType);
          return (
            <ModelTypeTag
              key={`${normalizedType}-${index}`}
              modelType={normalizedType}
              hideTooltip
            />
          );
        })}
      </span>
    );
  };

  const getSessionModelScopeLabel = (session: ChatSessionSummary): string | null => {
    if (session.modelName?.trim()) {
      return session.modelName.trim();
    }

    if (session.draftId?.trim()) {
      return `Draft ${session.draftId.trim()}`;
    }

    const modelKey = session.modelKey?.trim();
    if (!modelKey) {
      return null;
    }

    if (modelKey.startsWith('saved:')) {
      return modelKey.replace(/^saved:/, '');
    }

    if (modelKey.startsWith('draft:')) {
      return `Draft ${modelKey.replace(/^draft:/, '')}`;
    }

    return modelKey;
  };

  const renderModelScope = (session: ChatSessionSummary) => {
    const label = getSessionModelScopeLabel(session);
    if (!label) {
      return null;
    }

    return (
      <span
        className='assistant-history__model-scope'
        title={`Associated model: ${label}`}
      >
        <Icon icon='cube' />
        <span>{label}</span>
      </span>
    );
  };

  const handleResumeSession = async (session: ChatSessionSummary) => {
    setIfMounted(setActiveLoadingId, session.uuid);
    setIfMounted(setError, null);
    try {
      await loadChatSession(session);
      onSessionResume?.();
    } catch (err) {
      setIfMounted(setError, 'Unable to open that chat session.');
    } finally {
      setIfMounted(setActiveLoadingId, null);
    }
  };

  const loadChatSession = async (session: ChatSessionSummary) => {
    await chat.loadSessionFromHistory(session.uuid, {
      timestamp: session.timestamp,
      sessionName: session.sessionName,
      modelAssociation: {
        modelName: session.modelName,
        draftId: session.draftId,
        modelType: session.modelType,
        modelKey: session.modelKey,
      },
    });
  };

  const handleSessionUpdate = useCallback(async (sessionId: UUID, updates: Partial<Pick<ChatSessionSummary, 'sessionName' | 'isPinned' | 'modelTypes'>>): Promise<boolean> => {
    setIfMounted(setError, null);
    setIfMounted(setActionLoadingId, sessionId);
    try {
      const updated = await historyService.updateSession(sessionId, updates);
      setIfMounted(setSessions, (prev: ChatSessionSummary[]) =>
        prev.map(session => session.uuid === sessionId ? updated : session)
      );
      return true;
    } catch (err) {
      console.error('Failed to update chat session', err);
      setIfMounted(setError, 'Unable to update that chat right now.');
      return false;
    } finally {
      setIfMounted(setActionLoadingId, null);
    }
  }, [historyService, setIfMounted]);

  const stopActionClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const shouldShowOpenModel = (session: ChatSessionSummary): boolean => {
    const modelName = session.modelName?.trim();
    if (!modelName) {
      return false;
    }

    const sessionModelKey = session.modelKey?.trim() || `saved:${modelName}`;
    return !chat.isModelPage || chat.currentModelAssociation?.modelKey !== sessionModelKey;
  };

  const handleOpenModel = (event: MouseEvent<HTMLElement>, session: ChatSessionSummary) => {
    event.stopPropagation();
    const modelName = session.modelName?.trim();
    if (!modelName) {
      return;
    }

    void (async () => {
      setIfMounted(setActiveLoadingId, session.uuid);
      setIfMounted(setError, null);
      try {
        await loadChatSession(session);
        onSessionResume?.();
        dispatch(setCurrentModel({
          modelName,
          modelType: session.modelType ?? session.modelTypes?.[0] ?? undefined,
        }));
        navigateToModelRoute();
      } catch (err) {
        console.error('Failed to open associated model for chat session', err);
        setIfMounted(setError, 'Unable to open that chat session.');
      } finally {
        setIfMounted(setActiveLoadingId, null);
      }
    })();
  };

  const handleMenuSelect = (event: ChangeEvent<HTMLSelectElement>, session: ChatSessionSummary) => {
    event.stopPropagation();
    const action = event.target.value;
    // reset dropdown so the same action can be chosen again
    event.target.value = '';

    if (action === 'pin') {
      void handleSessionUpdate(session.uuid, { isPinned: !session.isPinned });
    }
    if (action === 'rename') {
      const nextName = window.prompt('Rename chat', session.sessionName);
      if (!nextName || !nextName.trim()) {
        return;
      }
      void handleSessionUpdate(session.uuid, { sessionName: nextName.trim() });
    }
    if (action === 'delete') {
      const confirmed = window.confirm(`Delete "${session.sessionName}"? This will remove the chat history.`);
      if (!confirmed) {
        return;
      }
      void (async () => {
        setIfMounted(setError, null);
        setIfMounted(setActionLoadingId, session.uuid);
        try {
          await historyService.deleteSession(session.uuid);
          setIfMounted(setSessions, (prev: ChatSessionSummary[]) =>
            prev.filter(s => s.uuid !== session.uuid)
          );
          if (chat.sessionId === session.uuid && typeof chat.handleReset === 'function') {
            chat.handleReset();
          }
        } catch (err) {
          console.error('Failed to delete chat session', err);
          setIfMounted(setError, 'Unable to delete that chat right now.');
        } finally {
          setIfMounted(setActionLoadingId, null);
        }
      })();
    }
  };

  const renderContent = () => {
    if (loadingSessions) {
      return (
        <div className='assistant-history__empty'>
          <Spinner size={20} />
          <span>Loading previous chats…</span>
        </div>
      );
    }

    if (error && filteredSessions.length === 0) {
      return (
        <div className='assistant-history__empty assistant-history__empty--error'>
          <Icon icon='warning-sign' intent='danger' />
          <span>{error}</span>
        </div>
      );
    }

    if (filteredSessions.length === 0) {
      return (
        <div className='assistant-history__empty'>
          <Icon icon='search' />
          <span>No chats match your search.</span>
        </div>
      );
    }

    const errorBanner = error ? (
      <div className='assistant-history__error-banner'>
        <Icon icon='warning-sign' intent='danger' />
        <span>{error}</span>
      </div>
    ) : null;

    return (
      <>
        {errorBanner}
        {filteredSessions.map(session => {
          const isActive = chat.sessionId === session.uuid;
          const isLoading = chat.isRestoringSession && activeLoadingId === session.uuid;
          const isActing = actionLoadingId === session.uuid;

          return (
            <Card
              key={session.uuid}
              interactive
              className={`assistant-history__card ${isActive ? 'assistant-history__card--active' : ''}`}
              onClick={() => handleResumeSession(session)}
            >
              <div className='assistant-history__card-header'>
                <div className='assistant-history__meta'>
                  <span className='assistant-history__date'>{formatDate(session.timestamp)}</span>
                  {renderModelScope(session)}
                  {renderModelPills(session.modelTypes)}
                </div>
                <div className='assistant-history__header-actions'>
                  {shouldShowOpenModel(session) && (
                    <Button
                      minimal
                      small
                      icon='share'
                      className='assistant-history__open-model-button'
                      text='Open model'
                      onClick={(event) => handleOpenModel(event, session)}
                    />
                  )}
                  {session.isPinned && <Icon icon='pin' className='assistant-history__pin-icon' />}
                  {isActive && <Tag minimal intent='primary'>Active</Tag>}
                  <div onClick={stopActionClick}>
                    <select
                      className='assistant-history__native-menu'
                      defaultValue=''
                      aria-label='Chat actions'
                      disabled={isActing}
                      onChange={(event) => handleMenuSelect(event, session)}
                    >
                      <option value='' disabled>⋮</option>
                      <option value='pin'>{session.isPinned ? 'Unpin' : 'Pin'}</option>
                      <option value='rename'>Rename</option>
                      <option value='delete'>Delete</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className='assistant-history__card-body'>
                <p className='assistant-history__title'>
                  {session.isPinned && <Icon icon='pin' className='assistant-history__title-icon' />}
                  {session.sessionName}
                </p>
                {isLoading || isActing ? (
                  <Spinner size={16} />
                ) : (
                  <Icon icon='chevron-right' color='#128F90' />
                )}
              </div>
            </Card>
          );
        })}
      </>
    );
  };

  return (
    <div className='assistant-history'>
      <div className='assistant-history__controls'>
        <InputGroup
          leftIcon='search'
          placeholder='Search'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className='assistant-history__search'
        />
        <select
          className='assistant-history__sort'
          value={sortBy}
          onChange={event => setSortBy(event.target.value as SortOption)}
        >
          {(Object.keys(sortLabels) as SortOption[]).map(option => (
            <option key={option} value={option}>
              {sortLabels[option]}
            </option>
          ))}
        </select>
      </div>
      <div className='assistant-history__list'>
        {renderContent()}
      </div>
    </div>
  );
}
